/**
 * A canvas-backed grid of colored cells — efficient for visualising thousands
 * of Bloom bits, HyperLogLog registers, or Count-Min counters at once. The
 * caller supplies a per-cell color function and calls `draw` to repaint.
 */

export interface HeatGridOptions {
  cellSize?: number;
  gap?: number;
}

export interface HeatGrid {
  canvas: HTMLCanvasElement;
  cols: number;
  rows: number;
  draw(color: (index: number) => string): void;
  indexAt(clientX: number, clientY: number): number | null;
}

export function heatGrid(cols: number, rows: number, options: HeatGridOptions = {}): HeatGrid {
  const cell = options.cellSize ?? 12;
  const gap = options.gap ?? 1;
  const cssW = Math.max(1, cols * (cell + gap) - gap);
  const cssH = Math.max(1, rows * (cell + gap) - gap);
  const dpr = Math.min((typeof window !== "undefined" && window.devicePixelRatio) || 1, 2);

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  canvas.className = "heatgrid";
  // The accompanying stats/tables convey the same information textually.
  canvas.setAttribute("aria-hidden", "true");

  const ctx = canvas.getContext("2d");
  if (ctx) ctx.scale(dpr, dpr);

  function draw(color: (index: number) => string): void {
    if (!ctx) return;
    ctx.clearRect(0, 0, cssW, cssH);
    const n = cols * rows;
    for (let i = 0; i < n; i++) {
      const c = i % cols;
      const r = (i / cols) | 0;
      ctx.fillStyle = color(i);
      ctx.fillRect(c * (cell + gap), r * (cell + gap), cell, cell);
    }
  }

  function indexAt(clientX: number, clientY: number): number | null {
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const c = Math.floor(x / (cell + gap));
    const r = Math.floor(y / (cell + gap));
    if (c < 0 || c >= cols || r < 0 || r >= rows) return null;
    return r * cols + c;
  }

  return { canvas, cols, rows, draw, indexAt };
}

/** Linear interpolate between two #rrggbb colors; t in [0, 1]. */
export function mixHex(a: string, b: string, t: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  const r = Math.round(ca[0] + (cb[0] - ca[0]) * t);
  const g = Math.round(ca[1] + (cb[1] - ca[1]) * t);
  const bl = Math.round(ca[2] + (cb[2] - ca[2]) * t);
  return `rgb(${r},${g},${bl})`;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}
